import './styles/message.scss';
import Matter from 'matter-js';

const wrapTextInSpans = () => {
  const fadeInElements = document.querySelectorAll('.js-word-fadein');
  
  fadeInElements.forEach(element => {
    // 既に処理済みの場合はスキップ
    if (element.classList.contains('text-wrapped')) return;
    
    // 既存のspan要素（.itemなど）を保持しつつ処理
    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue;
        if (!text.trim()) return; // 空白のみのテキストノードはスキップ
        
        // 日本語の句読点を判定する関数
        const isJapanesePunctuation = (char) => {
          // 日本語の句読点：、。、！？など
          return char === '、' || char === '。' || char === '！' || char === '？' || 
                 char === '，' || char === '．' || char === '：' || char === '；';
        };
        
        const parent = node.parentNode;
        const fragment = document.createDocumentFragment();
        let currentSpan = null;
        
        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          
          if (char === ' ' || char === '\n' || char === '\t') {
            // 空白文字はそのままテキストノードとして追加
            // 現在のspanがあればリセット
            currentSpan = null;
            fragment.appendChild(document.createTextNode(char));
          } else if (isJapanesePunctuation(char)) {
            // 日本語の句読点の場合は、直前のspanに追加
            if (currentSpan) {
              currentSpan.textContent += char;
            } else {
              // 直前のspanがない場合は新しいspanを作成
              currentSpan = document.createElement('span');
              currentSpan.textContent = char;
              currentSpan.classList.add('char-fade');
              fragment.appendChild(currentSpan);
            }
          } else {
            // 通常の文字は新しいspanを作成
            currentSpan = document.createElement('span');
            currentSpan.textContent = char;
            currentSpan.classList.add('char-fade');
            fragment.appendChild(currentSpan);
          }
        }
        
        // テキストノードを新しい要素で置き換え
        parent.replaceChild(fragment, node);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // 既存のspan要素（.itemなど）にもchar-fadeクラスを追加
        if (node.tagName === 'SPAN' && !node.classList.contains('char-fade')) {
          node.classList.add('char-fade');
        }
        
        // 子要素を再帰的に処理
        const childNodes = Array.from(node.childNodes);
        childNodes.forEach(child => processNode(child));
      }
    };
    
    // element直下の全ノードを処理
    const childNodes = Array.from(element.childNodes);
    childNodes.forEach(node => processNode(node));
    
    // アニメーション用のクラスを追加
    element.classList.add('text-wrapped');
    
    // この要素の全てのspan要素を取得（既存のspanも含む）
    const chars = element.querySelectorAll('.char-fade');
    element.setAttribute('data-char-count', String(chars.length));
  });
  
  // Intersection Observerでビューポート進捗を監視
  setupIntersectionObserver();
};


// ビデオエリアのマスクアニメーション
const setupVideoMask = () => {
  const videoAreas = document.querySelectorAll('.js-brand__video-area');
  
  const observerOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0
  };
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        updateVideoMask(entry.target);
      }
    });
  }, observerOptions);
  
  videoAreas.forEach(area => {
    observer.observe(area);
  });
  
  // スクロールイベントでマスクを更新
  window.addEventListener('scroll', () => {
    videoAreas.forEach(area => {
      updateVideoMask(area);
    });
  }, { passive: true });
};

// ビデオマスクの進捗を計算・更新
const updateVideoMask = (element) => {
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  
  // 要素の下辺と画面下部の交差を検出
  const elementBottom = rect.bottom;
  
  // 要素の下辺が画面内にある場合のみマスクを適用
  if (elementBottom > 0 && elementBottom <= windowHeight * 2) {
    // 進捗を計算（0-1）
    const normalizedProgress = (windowHeight - elementBottom) / windowHeight;
    const clampedProgress = Math.max(0, Math.min(1, normalizedProgress));
    
    // 進捗値をパーセントに変換
    const progressPercent = clampedProgress * 100;
    
    // マスクを有効化し、進捗と不透明度を設定
    element.classList.add('is-masking');
    element.style.setProperty('--mask-progress', `${progressPercent}%`);
  } else {
    // 範囲外の場合はマスクを無効化
    element.classList.remove('is-masking');
    element.style.removeProperty('--mask-progress');
  }
};



// 文字表示進捗を計算する共通関数
const calculateProgress = (element) => {
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const triggerLine = windowHeight * 0.6; // 画面上部から60%の位置
  
  // 要素の上端と下端
  const elementTop = rect.top;
  const elementBottom = rect.bottom;
  const elementHeight = rect.height;
  
  let progress = 0;
  
  // 要素の上端がトリガーラインより下にある場合（まだ到達していない）
  if (elementTop > triggerLine) {
    progress = 0;
  }
  // 要素の下端がトリガーラインより上にある場合（通り過ぎた）
  else if (elementBottom < triggerLine) {
    progress = 1;
  }
  // 要素がトリガーラインを跨いでいる場合
  else {
    // 要素の上端がトリガーラインを通過してからの距離を計算
    const distanceFromTop = triggerLine - elementTop;
    // 要素の高さに対する進捗率を計算
    progress = distanceFromTop / elementHeight;
  }
  
  return Math.min(1, Math.max(0, progress));
};

// 文字を表示/非表示にする関数
const revealCharacters = (element) => {
  const chars = element.querySelectorAll('.char-fade');
  const totalChars = chars.length;
  const progress = calculateProgress(element);
  const charsToShow = Math.floor(totalChars * progress);
  
  chars.forEach((char, index) => {
    if (index < charsToShow) {
      // 表示する文字
      char.classList.add('is-visible');
    } else {
      // 非表示にする文字（スクロールを戻した時）
      char.classList.remove('is-visible');
    }
  });
};

const setupIntersectionObserver = () => {
  const fadeInElements = document.querySelectorAll('.js-word-fadein.text-wrapped');
  
  // Intersection Observer設定
  const observerOptions = {
    root: null,
    rootMargin: '0px',
    threshold: (() => {
      const thresholds = [];
      for (let i = 0; i <= 100; i++) {
        thresholds.push(i / 100);
      }
      return thresholds;
    })()
  };
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      // ビューポート外に出た場合も処理する
      revealCharacters(entry.target);
    });
  }, observerOptions);
  
  // 各要素を監視
  fadeInElements.forEach(element => {
    observer.observe(element);
  });
  
  // スクロールイベントでも進捗を更新（より滑らかな動作のため）
  let scrollTimer;
  window.addEventListener('scroll', () => {
    // パフォーマンス最適化のため、スクロール中は間引く
    if (scrollTimer) return;
    
    scrollTimer = setTimeout(() => {
      fadeInElements.forEach(element => {
        revealCharacters(element);
      });
      scrollTimer = null;
    }, 10);
  }, { passive: true });
};

// スクロール進捗を取得する関数
const getScrollProgress = () => {
  const startElement = document.querySelector('.p-message__video');
  const endElement = document.querySelector('.p-message__video__body');
  
  if (!startElement || !endElement) {
    console.warn('Elements not found:', { startElement, endElement });
    return 0;
  }
  
  const windowHeight = window.innerHeight;
  const scrollY = window.pageYOffset || document.documentElement.scrollTop;
  const startPos = startElement.getBoundingClientRect();
  const endPos = endElement.getBoundingClientRect();
  const startScrollPosition = startPos.top + scrollY + windowHeight;
  const endScrollPosition = endPos.top + scrollY + windowHeight/2;
  
  // endScrollPositionがstartScrollPositionより小さい場合の対処
  if (endScrollPosition <= startScrollPosition) {
    // 要素が近すぎる、または同じ位置にある場合は、スクロール位置で判定
    return scrollY >= startScrollPosition ? 1 : 0;
  }
  
  const totalDistance = endScrollPosition - startScrollPosition;
  
  // 進捗を計算（0〜1の範囲）
  if (scrollY <= startScrollPosition) {
    return 0;
  } else if (scrollY >= endScrollPosition) {
    return 1;
  } else {
    const currentDistance = scrollY - startScrollPosition;
    const progress = currentDistance / totalDistance;
    return Math.max(0, Math.min(1, progress)); // 0〜1にクランプ
  }
};

// スクロール進捗を監視して表示する関数
const initScrollProgressTracking = () => {
  let rafId = null;
  const updateProgress = () => {
    const progress = getScrollProgress();
    videoEffect(progress);
  };  
  // スクロールイベントリスナー
  const handleScroll = () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
    rafId = requestAnimationFrame(updateProgress);
  };  
  // 初期値を表示
  updateProgress();
  // スクロールイベントを登録
  window.addEventListener('scroll', handleScroll, { passive: true });
  // リサイズ時も更新
  window.addEventListener('resize', handleScroll, { passive: true });
};

const videoEffect = (progress) => {
  if (progress > 0) {
    const playButton = document.querySelector('.p-message__video__play');
    playButton.classList.remove('is-visible');
  } else {
    const playButton = document.querySelector('.p-message__video__play');
    playButton.classList.add('is-visible');
  }
  const video = document.querySelector('.js-brand__video video');
  const videoWrapper = document.querySelector('.js-brand__video');
  videoWrapper.style.transform = `scale(${1 + progress * 0.6})`;
  video.style.transform = `scale(${1 + progress * 1.2})`;
  video.style.filter = `blur(${progress * 100}px)`;
  video.style.opacity = `${1 - progress * 0.95}`;
};


// DOMContentLoadedで実行
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    wrapTextInSpans();
    setupVideoMask();
    initScrollProgressTracking(); // スクロール進捗トラッキングを初期化
  });
} else {
  // 既に読み込み済みの場合は即実行
  wrapTextInSpans();
  setupVideoMask();
  initScrollProgressTracking(); // スクロール進捗トラッキングを初期化
}

export default wrapTextInSpans;

 /**
     * 単語物理演算クラス
     * Matter.jsを使用してDOM要素に物理演算を適用
     */
 class WordPhysics {
  // Matter.js モジュール
  static Matter = window.Matter;
  static Engine = Matter.Engine;
  static Bodies = Matter.Bodies;
  static Body = Matter.Body;
  static Composite = Matter.Composite;
  static Mouse = Matter.Mouse;
  static MouseConstraint = Matter.MouseConstraint;

  /**
   * @param {Object} config - 設定オブジェクト
   * @param {HTMLElement} config.container - 単語を配置するコンテナ要素
   * @param {Array} config.words - 単語データの配列
   * @param {Array} config.styles - スタイルクラス名の配列
   */
  constructor(config) {
    this.container = config.container;
    this.words = config.words;
    this.styles = config.styles;

    // 物理演算関連
    this.engine = null;
    this.mouseConstraint = null;
    this.animationId = null;

    // DOM要素と物理ボディの管理
    this.wordBodies = [];
    this.wordElements = [];

    // 設定
    this.gravityEnabled = false;
    this.rotationRange = { min: 5, max: 45 };
    this.gridCols = 4;

    // イベントバインド
    this._update = this._update.bind(this);
  }

  // ユーティリティメソッド
  _randomInRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  _randomRotation() {
    const sign = Math.random() > 0.5 ? 1 : -1;
    const { min, max } = this.rotationRange;
    return sign * this._randomInRange(min, max) * (Math.PI / 180);
  }

  _shuffle(array) {
    return [...array].sort(() => Math.random() - 0.5);
  }

  // DOM要素の作成
  _createWordElement(word, index, style) {
    const el = document.createElement('div');
    el.className = `word-item ${style}`;
    el.innerHTML = word.title.replace(/\n/g, '<br>');
    el.dataset.index = index;
    el.dataset.originalIndex = this.words.indexOf(word); // 元の配列でのインデックスを保存

    const fontSize = this._randomInRange(word.sizeRange[0], word.sizeRange[1]);
    el.style.fontSize = `${fontSize}px`;
    el.style.fontWeight = 'bold';
    el.style.cursor = 'pointer'; // クリック可能であることを示す

    // ドラッグ検出用の変数
    let isDragging = false;
    let startX = 0;
    let startY = 0;
    const dragThreshold = 5; // ピクセル単位の移動閾値

    // マウスダウンイベント
    el.addEventListener('mousedown', (e) => {
      startX = e.clientX;
      startY = e.clientY;
      isDragging = false;
    });

    // マウスムーブイベント（ドラッグ検出）
    el.addEventListener('mousemove', (e) => {
      if (e.buttons === 1) { // マウスボタンが押されている場合
        const deltaX = Math.abs(e.clientX - startX);
        const deltaY = Math.abs(e.clientY - startY);
        
        // 閾値を超えて移動した場合はドラッグとみなす
        if (deltaX > dragThreshold || deltaY > dragThreshold) {
          isDragging = true;
        }
      }
    });

    // クリックイベント（ドラッグでない場合のみモーダル表示）
    el.addEventListener('click', (e) => {
      e.stopPropagation(); // バブリングを防ぐ
      
      // ドラッグ操作の場合はモーダルを開かない
      if (!isDragging) {
        const originalIndex = parseInt(el.dataset.originalIndex);
        if (window.wordModal) {
          window.wordModal.open(originalIndex);
        }
      }
      
      // リセット
      isDragging = false;
    });

    return el;
  }

  // コンテナのクリア
  _clearContainer() {
    Array.from(this.container.children).forEach(child => {
      if (child.id !== 'loadingMsg') {
        this.container.removeChild(child);
      }
    });
    this.wordBodies = [];
    this.wordElements = [];
  }

  // 物理エンジンのクリア
  _clearEngine() {
    if (this.engine) {
      WordPhysics.Engine.clear(this.engine);
    }
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  // 壁の作成
  _createWalls(width, height) {
    const thickness = 60;
    const { Bodies, Composite } = WordPhysics;

    const walls = [
      Bodies.rectangle(width / 2, height + thickness / 2 - 1, width + thickness * 2, thickness, {
        isStatic: true,
        label: 'wallBottom'
      }),
      Bodies.rectangle(width / 2, -thickness / 2 + 1, width + thickness * 2, thickness, {
        isStatic: true,
        label: 'wallTop'
      }),
      Bodies.rectangle(-thickness / 2 + 1, height / 2, thickness, height + thickness * 2, {
        isStatic: true,
        label: 'wallLeft'
      }),
      Bodies.rectangle(width + thickness / 2 - 1, height / 2, thickness, height + thickness * 2, {
        isStatic: true,
        label: 'wallRight'
      }),
    ];

    Composite.add(this.engine.world, walls);
  }

  // スタイル割り当ての生成
  _generateStyleAssignments(count) {
    const assignments = [
      ...this.styles,
      ...Array(count - this.styles.length).fill('style-plain')
    ];
    return this._shuffle(assignments);
  }

  // 初期位置の計算
  _calculatePosition(index, total, width, height, elWidth, elHeight) {
    const margin = 50;
    const minX = elWidth / 2 + margin;
    const maxX = width - elWidth / 2 - margin;
    const minY = elHeight / 2 + margin;
    const maxY = height - elHeight / 2 - margin;

    const rows = Math.ceil(total / this.gridCols);
    const col = index % this.gridCols;
    const row = Math.floor(index / this.gridCols);

    const cellWidth = (maxX - minX) / this.gridCols;
    const cellHeight = (maxY - minY) / rows;

    const baseX = minX + cellWidth * col + cellWidth / 2;
    const baseY = minY + cellHeight * row + cellHeight / 2;

    return {
      x: baseX + this._randomInRange(-cellWidth * 0.3, cellWidth * 0.3),
      y: baseY + this._randomInRange(-cellHeight * 0.3, cellHeight * 0.3)
    };
  }

  // 物理ボディの作成
  _createPhysicsBody(x, y, width, height, angle) {
    return WordPhysics.Bodies.rectangle(x, y, width, height, {
      angle,
      restitution: 0.3,
      friction: 0.6,
      frictionAir: 0.02,
      chamfer: { radius: 8 }
    });
  }

  // マウス操作の設定
  _setupMouseControl() {
    const { Mouse, MouseConstraint, Composite } = WordPhysics;

    const mouse = Mouse.create(this.container);
    this.mouseConstraint = MouseConstraint.create(this.engine, {
      mouse,
      constraint: {
        stiffness: 0.2,
        render: { visible: false }
      }
    });

    Composite.add(this.engine.world, this.mouseConstraint);
    mouse.element.removeEventListener('wheel', mouse.mousewheel);
  }

  // アニメーションループ
  _update() {
    WordPhysics.Engine.update(this.engine, 1000 / 60);
    this._updatePositions();
    this.animationId = requestAnimationFrame(this._update);
  }

  // DOM要素の位置更新
  _updatePositions() {
    this.wordBodies.forEach((body, i) => {
      const el = this.wordElements[i];
      if (!el) return;

      const { x, y } = body.position;
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.transform = `translate(-50%, -50%) rotate(${body.angle}rad)`;
    });
  }

  // 公開メソッド

  /**
   * 初期化・再配置
   */
  init() {
    const loadingMsg = document.getElementById('loadingMsg');
    if (loadingMsg) loadingMsg.style.display = 'none';

    this._clearContainer();
    this._clearEngine();

    const { width, height } = this.container.getBoundingClientRect();

    // エンジン作成
    this.engine = WordPhysics.Engine.create();
    this.engine.gravity.y = this.gravityEnabled ? 0.5 : 0;

    // 壁の作成
    this._createWalls(width, height);

    // 単語とスタイルのシャッフル
    const shuffledWords = this._shuffle(this.words);
    const styleAssignments = this._generateStyleAssignments(shuffledWords.length);

    // DOM要素の作成
    const elements = shuffledWords.map((word, i) => {
      const el = this._createWordElement(word, i, styleAssignments[i]);
      el.style.visibility = 'hidden';
      el.style.left = '0px';
      el.style.top = '0px';
      this.container.appendChild(el);
      return el;
    });

    // 物理ボディの作成
    elements.forEach((el, i) => {
      const rect = el.getBoundingClientRect();
      const elWidth = rect.width + 15;
      const elHeight = rect.height + 15;

      const pos = this._calculatePosition(i, shuffledWords.length, width, height, elWidth, elHeight);
      const angle = this._randomRotation();

      const body = this._createPhysicsBody(pos.x, pos.y, elWidth, elHeight, angle);
      body.elementIndex = i;

      WordPhysics.Composite.add(this.engine.world, body);

      this.wordBodies.push(body);
      this.wordElements.push(el);
      el.style.visibility = 'visible';
    });

    // マウス操作の設定
    this._setupMouseControl();

    // アニメーション開始
    this._update();
  }
}

// ===== アプリケーション初期化 =====

// Matter.js 読み込み確認
if (typeof Matter === 'undefined') {
  document.getElementById('loadingMsg').textContent = 'Error: Matter.js の読み込みに失敗しました';
  throw new Error('Matter.js not loaded');
}

document.getElementById('loadingMsg').style.display = 'none';

// 単語データ
const words = [
  { title: '真実の瞬間', description: '飲食店で外見はきれいなお店なのに机の端が汚れている席を見て、その店全体の印象を悪く思ってしまったことは無いだろうか？逆に、こちらから何も言わなかったのにもかかわらず、食後に新しいおしぼりを出してもらった事で店全体の印象を良く思ったなどの経験はないだろうか？人間は良いことも悪いことも、一瞬の印象が全体のイメージを決めてしまうものである。その瞬間こそ「真実の瞬間」なのだ。顧客の期待に応えるためには、瞬時の機転と的確な状況判断が必要である。それには日々「真実の瞬間」を意識すべきである。顧客は「数秒間」で判断し、それが顧客のその後の態度をも決めてしまう。「真実の瞬間が永遠になる」のである。顧客は商品やサービスを買うのではなく、「期待」を買うのである。感動の「真実の瞬間」を待ち望んで契約を交わすといっても過言ではない。', sizeRange: [16, 20] },
  { title: '思い切りバットを振る', description: '通常、我々が自分と思っているのは理性で把握できる自分であり、本当は自分の中に無限大の可能性が広がっている。新しい事にチャレンジする時は、必ず壁に当たったり、計画通りに運ばなかったりするだろうが、これは失敗ではなく、一時的な挫折なのである。失敗を失敗と捉えるのではなく、あくまで途中経過と考え、そこから成功へのヒントを学んでいく「失敗上手」な人間が成功を一番早くつかめるのである。思い切りバットを振るとは自分の可能性を信じ、失敗を恐れるなという事を意味している一方で、素直に現実を受け入れない限り、失敗から学ぶ事は出来ず、その状態でいくらバットを振っても無駄である事を忘れてはならない。新しい事にチャレンジし、挫折し、知識や経験を蓄えて再度チャレンジする。アイル社員はこのスタンスを大切にしている。', sizeRange: [12, 16] },
  { title: '価値の連鎖', description: '当たり前のことだが、どんな人間も１人では生きていくことが出来ない。会社も同様に多くの企業と関わることで成り立つ。仕事とは他人に役立つことを行い、役に立つということは価値を人に与えることである。そして価値はお客様に与えているが、最終的には自分に返ってくるのである。自分のために仕事をする人、相手に関係なく自分が納得すれば良いという考えの人では相手に価値を与えるなんて出来ないだろうし、自分に返ってくる価値も小さい。しかし、逆の人はどうだろうか。お客様に価値と満足を与えれば、会社に利益という価値をもたらす、そして自分にも満足と報酬という形で戻ってくる。自分・企業・社会に価値・利益・喜びという「価値の連鎖」が起こる。この連鎖を起こすことが、人生の好循環へと繋がっていくのだ。「価値の連鎖」を自覚し、実行することで「仕事の価値」が高まり、それを高めることで仕事に対する充実感や満足感がひと一倍得られるのである。そうすると、いつのまにか仕事と遊びとの境が無くなり、仕事が楽しくなり、好循環の素晴らしい人生を過ごすことができるのである。 仕事は「やりたいこと」をやるのではない。「やらなければならないこと」をやって、１２０%の成果を出さなければならない。やりたいことを追い求める人は、困難な壁を避けて通る人である。困難な壁を避ける行為もまた自己中心的なものであるということを肝に命じる必要がある。', sizeRange: [20, 28] },
  { title: '勝つべくして勝つ', description: '企業活動において結果良ければすべて良しと考えることは、非常に短絡的であり、勝利の裏で科学的な裏づけをとる事はとても大事である。科学的な裏づけとは、企業にとって重要な「勝つ為のしくみ」を作ることであるが、そこで求められるのは、まず5年後10年後市場はどうなり、自分達はどうなるべきなのかを考える「先見力」がある。そして、｢先見力｣と共に大事になってくるのが、「創造・発想力」である。アイルでは創造・発想力を高め付加価値を作り出すことで独自性、優位性を保っているが、付加価値とは企業の個性である。本物の個性を生み出す為には、まずは徹底的に基本を鍛えることが重要である。そして基本を身に付けた後その殻を破り、最後に基本と全く離れた「独創性」を生み出す事が出来るのである。この「守・破・離」のプロセスを経て、アイル独自の勝つ仕組みを形成しているのである。', sizeRange: [12, 16] },
  { title: 'マーケットアウト', description: '企業がモノを作り、その後それをどのマーケットに投入するかを決めることを「プロダクトアウト」というが、その逆の発想である「マーケットアウト」はビジネスの起点をマーケット（顧客）に求めるものである。「マーケットアウト」の発想の下では、優先されるのはマーケット（顧客）そのものであり、マーケット（顧客）が本当に求めていることに基づいて、商品やサービスの開発を行わなければならない。事業創出の起点を、従来の「プロダクトアウト」の正反対の位置から出発するのが「マーケットアウトビジネス」である。マーケット（顧客）とは顧客ニーズのことであり、それは具体的には顧客が抱えている不便や不満のことである。ただ、顧客が自らのニーズに気づいていないケースが大半であり、そうした潜在的なニーズの掘り起こしも我々の大切な役割であると思っている。そうした顧客ニーズは顧客の視点で考えることで初めて気づけるものである。そのニーズに基づいて今度はどんなソリューションが提供できるかを考えていくことがビジネスチャンスに繋がり、事業を継続的に繁栄させるものと思っている。それと同時に、意識してニーズを掘り下げる目を養うことが、我々の提案力・サポート力の強化に繋がっていくのである。', sizeRange: [16, 22] },
  { title: '自律と責任', description: '最高の仕事を引き出すためには、人は動機づけが必要となる。いかなる動機づけが必要であるのかを企業のトップに聞くと、多くのトップは「社員の満足」と答える。アイルでは仕事への積極的な動機づけとなるのは、「社員の満足」はあたり前としてその上で、資質の向上や習慣的能力を高める努力などと同様に、受け身でない自立（自律）による「責任」であると考える。社員の「士気」を高める「動機」は本人の自律によるところが大きい。従って、企業は社員に対して、「責任」を持つように励まし、仕事が立派に行われるようにしなければならないのである。権限を今以上に委譲し、社員の自立(自律)を促進することが、企業の発展に繋がるのである。<br>仕事のやりがいは、現状に疑問を向け、自分が何かを変えられないかを考えることによって芽生える。「変えよう」という思いが確かになった時、仕事に主体性が生まれるのである。自分の仕事に納得し、意欲をもって働くことが豊かな人生につながるのである。そして「自分は成長したい」の欲求も誰もが持ち合わせている。努力し、向上し、達成する、誰もが当たり前に望んでいることである。お互いの仕事を認め合える環境が「働きがい」につながるのである。', sizeRange: [22, 30] },
  { title: '無駄の合理性', description: '我々は、仕事の手を止めてまでも創業時から続けていることがある。企業文化でもある月報会議や会食、創立記念式典などがこれにあたる。また、新人研修ひとつとっても、技術・ノウハウや商品知識をただ詰め込むようなことではなく、「人」として、「企業人」としての育成に時間をかける。合理性だけを追及しようとすると、これらは無駄なことに思えるかもしれない。しかし、一見無駄のように思われるこれらは、我々にとっては決して単なる無駄とは思わない。会社の業績や将来のビジョン、各部署の取り組み、そして想いをトップ自らが全社員に向けて発信することで、社員ひとりひとりが仕事に対して決して受け身でなく、自ら考え、そして行動できるようになるからである。これがアイルの現場力の強さに繋がっているのだ。', sizeRange: [12, 16] },
  { title: '細部にこそ神は宿る', description: '本当の一流は、誰も見ないような細かい所にこだわる。ある一流アミューズメントパークでは、照明にキャラクターのロゴを入れていたり、小さな小窓の向こう側にさらに面白い仕掛けがしてあったことを覚えている。気付かない人には気付かない仕掛けだが、こういった小さな仕掛けの積み重ねが、一流を形成していくのである。そして仕掛けに気付いた人間は大きな驚きと感動を味わうことが出来る。企業も同じで、一流企業は誰も気付かないような提案書のレイアウトであったり、お客様への何気ない一言など小さな事に細心の注意を払う。アイルでも社員同士の挨拶にはじまり、提案書の一文字、そしてお客様への対応と徹底して細部にこだわっている。それは細部にこそ神は宿り、そこにこだわるかこだわらないかで会社の未来が変わってくることを知っているからだ。一つ一つは、小さな積み重ねかもしれない。しかし、そこにこだわる事で、神様はこちらに微笑んでくれるのである。', sizeRange: [12, 16] },
  { title: 'どの道を選ぶかより\n選んだ道でどう生きるか', description: '生きている上で自分の運命を左右するような決断をくださねばならない時がある。例えば、就職活動などもその一つである。非常に多くの企業の中から本当に自分に合う企業を一社決めなければならない。しかし、少し乱暴な言い方をすれば、本当に重要なことは、どの企業に決めるかではなく、選んだ企業でどれだけ、がんばって成長できるかが最も重要ではないだろうか？人は常に自分がやりたいことを考えてしまう為、理想と現実のギャップに悩んだり、自分の求めている道は違うところにあるはずだと思い込んでしまうのだ。しかし、これは大きな間違いなのである。幸せの青い鳥は探しても見つからない。自分が一生懸命に歩き、その道の先で向こうから自分に寄ってくるのである。自分の歩く道ばかりに気を使うのではなく、選んだ道でどう頑張っていくかという事が何よりも大切なのだ。', sizeRange: [11, 14] },
  { title: 'あたり前のことをあたり前に', description: '「誰にでもできるあたり前のことを、誰でもできないくらい続ける。やがてそれが習慣になり個性になる。」あたり前のことをあたり前に行っていけば、それが結果として自己差別化に結びつくのである。そして、当たり前のことを継続することは、けっして容易なことではないが、だからこそあたり前のことを徹底して実践すれば、逆に当たり前のことではなくなるのである。経営の原点は常に基本の徹底であり、これができてこそ初めて変化への対応にも可能になるのだということは、日頃から常に思っていることである。我が社には創業以来から毎月継続している月報会議が存在する。地味な作業であるが、この月報会議の意義は大きく、社員全員がこれらを共有することで、他社には真似の出来ない集団としてのエネルギーを持っている。「あたり前のことをあたり前にする」、この継続こそが我が社の強みであると思っている。<br>毎年選手が入れ替わる学生スポーツの連覇はことのほか難しいが、不思議なことにメンバーが入れ替わっても名門といわれるチームは必ず勝つ。強さと部員の姿勢は無縁ではないだろう。チームとしての方針、思考、環境、文化、誇り、憧れ、これらが部員を鍛え、成長させるのである。このような強い組織には、共通したものがある。それは当たり前のことを徹底的に叩き込まれ、完璧に遂行する力が養われる環境である。環境が仕事の「質」を向上させ、その「徹底度」でレベルが決まる。気が付いたことには全て対処し、やり残しの部分を作らないことである。効率を上げることを目標に、努力し、工夫し続けることで、他人より量をこなせるようになり、本人の実力が底上げされるのである。また、仕事に集中できない人は結果を出せない。集中できない人は、「集中する能力」が欠けているのではなく、「自分が集中できる環境をつくる能力」が欠けているのである。睡眠を十分に取ること、騒々しい場所で仕事をしないこと、時間を区切って仕事をすること、整理整頓すること、「朝型生活」にシフトし、仕事を早く切り上げるように努めること、朝食はしっかり取ること、等々である。特別なことは何もない。小さな努力の積み重ねで集中力を高めることができるのである。', sizeRange: [12, 16] },
  { title: '不安のお化け', description: '不安の多くは自分自身の想像で作られる。悩んでいる時は、自分の悩みを自由に発言し、ぶちあたっている壁や苦しみをオープンにしてみると、皆で共有することで不安が少しずつ取り除くことが出来る。そして、不安に思い切ってぶつかってみるとその不安は案外小さなものであり、結局大きなお化けに作り上げていたのは自分自身の心であったと気づくはずである。目の前にある問題や不安は本当に大きなことなのかどうか、恐れずに思い切ってぶつかってみることが大切なのである。', sizeRange: [18, 24] },
  { title: '快の追求', description: '「楽しんで仕事をする」ということは「好きだから得意、得意だから好き」の好循環サイクルに乗ってはじめて可能になるものである。我が社は創業以来、ずっと「快」を追求してきた。それは、人は誰でも幸せな気持ちで仕事をしたいと願っているし、また仕事も「ワクワクした気持ち」でやることで効率も上がるからである。「快」とは、楽しい、嬉しいといったポジティブな感情で、人が何か事を起こす際、その根底には無意識に必ずこの「快」の欲求があると思っている。しかし、「快」の状態を継続させることは、口で言うほど簡単ではない。何故なら「快」と「不快」は表裏一体のものでもあるからである。いくら「快」を求めようとしても、考え方、受け止め方、要するに気持ちの持ち方ひとつで、簡単に「不快」が顔をのぞかせることになる。人は色んな不快に出会っている。しかし、その度にそれを乗り越えなくてはならない。環境にうまく適応でき、すべてを「快」にできる能力、どんな時でも「快」を見つけられる能力を身に付けなくてはならないのである。仕事とは本来、辛く苦しい「不快」の方が圧倒的に多い。その中で「快」を感じられることは、神様からのプレゼントのようなものである。このプレゼントを受け取る資格を得るには努力なしでは有り得ない。本当に血の滲むような努力を積み重ねた者だけに与えられる特権なのである。まだ社会に出て数年の若手社員は、仕事から得られる「快」を求めても、「快」を感じる資格はなく「不快」ばかりが顔をのぞかせることだろう。しかしそれは当然のことであり、これを乗り越えるために「心力」を養う必要があると思っている。「心力」には、正しいモノの「見方」、「考え方」、要するに「理念」、「概念」が大事である。我が社がこれらの「理念」、「概念」を重要視するのは、「快」の追求を目的とすることに他ならない。 結局は自分次第であり、そこにいるだけで仕事を楽しめる環境はこの世には存在しない。それは、アイルであっても同様である。しかし、個人が不快の壁を乗り越え「快」を追求するための環境提供と手助けを、会社として最大限行いたいと思う。ほんとうに仕事を「楽しめるのか？」その答えが「自分次第」であることを知っている者だけが「快」を得ることが出来るのである。', sizeRange: [14, 18] },
  { title: '秩序ある混沌', description: '組織にとって大事なのは個人の力を活かすことだ。しかし皆が好きなようにする、それでは「混沌とする」であろう。だから「秩序」は大切にする。全員が同じ方向を向いて日々取り組んでいるからこそ、社員ひとりひとりが思い思いに仕事に取り組んでも、組織全体に「秩序」がある。それが「秩序ある混沌」だ。自由だからこそ、自ら創造し、そして素晴らしいアイデアがひらめく。企業規模が大きくなるにつれ、組織は硬直的になりやすくなるかもしれない。しかし、どれだけ大きな会社になろうとも「秩序ある混沌」を堅持していくことが大切なのである。', sizeRange: [11, 14] },
  { title: '無意識の中の意識', description: '本を読んでいて、いつの間にか１０ページも読んでいるのに全く内容は頭に入っていないという経験をしたことはないだろうか？表面的には文章を読んでいても心の底では、全く関係のないことを考えているような時である。このように、気がつくといつの間にか考えてしまっているような状態を「無意識の中の意識」と呼ぶ。１つのことを考える時、まとまった時間をとってひたすら考えるよりも、心身の緊張を取り、頭の中をからっぽにして脳を休ませ「無意識の中の意識」の状態を作ることでアイデアを無理やり出すのではなく、自然と出す環境を作ることが大切であり、いつの間にか考えている状態の時にこそ素晴らしいアイデアが閃いたりするのだ。会議室に集まり延々と会議をするようなやり方は、アイルでは行わない。', sizeRange: [18, 24] },
  { title: '止揚', description: '企業経営において、「利益追求」と「社会貢献」などの矛盾にしばしば直面することがある。企業が抱える矛盾を機械的な割り切りによって解消してしまうと、同時に生命力や原動力も消えて、発展が止まってしまう。世の中のすべての物事の進歩や発展は、単純に右上がりに一直線に進歩、発展するわけではなく、あたかも螺旋階段を登るようにして進歩、発展していく。現実は矛盾に満ち、複雑であり、曖昧な要素があまりにも多い。すべての物事には、その内部に「矛盾」が含まれているが、その矛盾こそが物事の発展の原動力となっていく。「止揚」とは、お互いに矛盾し、対立するかに見える２つのものに対して、いずれか一方を否定するのではなく、両者を肯定、包括、統合し、超越することによって、より高い次元のものへと昇華していくことである。物事の本質を洞察し、未来を予測するには、「分析思考」や「論理思考」だけでは有効ではなく、「哲学的思索」も必要であり、そのひとつが「止揚」である。', sizeRange: [18, 24] },
  { title: '創発人材', description: '「創発」とは、主に複雑系の科学で用いられる用語であり、本来はふらふらしている局所的な複数の細胞が、相互作用によって複雑に組織化することで、環境変化に適応する高度な組織・システムを生み出す現象・機能を意味する。このような創発を組織に生み出す人材を「創発人材」という。「蛍」が清水を好むのと同様に、彼らは自由で明るい自律的な企業風土を好み、そういった環境でないと生息することができない。「創発人材」はふらふらしていて一見は「いい加減」に生きているように見える。どんな組織でもある割合で普通に生息している「少数派の生き物」であるが、環境が整えば抜群の創発力を発揮する。しかし普通は目立たず、評価されない。また従来の人事管理では優れた人材として評価されない可能性がある。もし組織において、大手企業などに見られるような管理的手法が蔓延している環境にいると、「創発人材」である彼らの活動や働きは大きく鈍ることになる。現場でイノベーションを生み出すためには、「創発人材」を活かし、彼らが好む「快」の最適な創発組織風土をつくることが必要である。イノベーションは、育てるものである。イノベーションを育てるためには、できるだけ自律した環境が重要である。自律とは、自分たちが主体的に自分たちの規範をつくって、それに従って行動することである。自身を自分たちでマネージメントすることである。イノベーション創出力＝組織の自律性×リーダーの牽引力である。イノベーションを作り出す力は、このような式で表すことができる。アイルには多くの「創発人材」が存在し、彼らがイノベーションを発揮する環境がある。これがアイルの本質であると思っている。', sizeRange: [14, 18] },
  { title: '未来は先にあるのではなく\n自分の中にある', description: '未来は自分の性格や、モノの見方、考え方によって決まる。未来に向かって敷かれたレールはない。道は自分でつくるものである。ビジネスでいえば、レールが敷かれていない未来に向かって踏み出すために必要なのは、「発想する力」である。今はない状態から、新しいものを生み出す、いわば「無」から「有」を生む発想力である。１歩先の未来像を描き、未来を起点として発想する。そして、未来像を実現するため、世の中にすでにあるものを結びつけて、新しい価値を生み出すのである。ユーザー顧客が不便、不満、不都合に感じていることについて、どうすればこれを便利、満足、好都合に転換できるか、１歩先の未来像を描き、それを実現するためには、何と何を結びつければ新しい価値が生まれるかを考えるのである。人間は何かに挑戦しようとすれば、「成功させよう」という思いがわいてくる。そして同時に「失敗してはいけない」という思いも抱いてしまう。「失敗してはいけない」と思うと、どこかで従来のものを守ろうとする意識が働き、過去の延長線上から飛び出すことができなくなってしまう。「失敗してもすぐに忘れて仕切り直せばいい」と思えば、１歩先へとジャンプして発想できる。いくつか挑戦して、その中にあることに成功すると、「これができたのだから今度はこれに挑戦してみよう」と次の段階へと進み、最後には成功するのだと思う。「本質」を見抜けばビジネスはうまくいくのである。', sizeRange: [12, 16] },
  { title: '手段の目的化', description: '花に水をあげるのは何のためにするのか。答えは花を枯らさないためである。しかし、仮に枯れてしまったらどうだろうか。多くの人はしっかりと水をあげていたのに・・・と考えるだろう。しかし目的は枯らさない為であり、水をあげるのは単なる手段に過ぎない。光を浴びていなかったり、寿命で枯れてしまったり原因は他にあるかもしれないが、それに気付かないのは当初の目的を逸していることになる。これは、日々の企業活動にも言えることであり、沢山の業務を行っていると、いつの間にか今行っていることが目的になってしまう場合がある。例え話のように水をあげるという手段が、いつの間にか目的になってしまわないよう、日々自分の業務を振り返り、今の業務の目的を検証することが大切なのである。', sizeRange: [12, 16] },
  { title: 'フィフティ・フィフティ', description: '「お客様は神様である」という一般的な考えはアイルでは当てはまらない。お客様とは、「フィフティ・フィフティ」であるというのがアイルの考え方である。これを言うとお客様に対して、偉そうにしていると思われるかもしれないがそうではなく、お客様と本当の意味で信頼関係を築いて、長期的なお取引をしようと思えばこそ、この考え方が大切になると考えている。同じ価値観を持ち、お互いに、ビジネスを創り上げていくという信頼関係があって、初めて友好的なビジネスが成立するのである。「クレーム処理産業」と言われてきたIT業界において、アイルほどクレームの少ない企業は稀であろうが、これは従来から行ってきた「選別受注」に起因しており、これがアイルの安定した成長に繋がっているといっても過言ではない。また、これはお客様のみならず、社員に対してもフィフティ・フィフティだと考えている。社長であろうが、新入社員であろうが人としての「立場」は同じで「役割」が違うだけ。「真実」は「権力」にも勝る。これがアイルが創業以来から貫き通してきた考え方である。', sizeRange: [16, 22] },
];

// モーダル管理クラス
class WordModal {
  constructor(wordsData) {
    this.words = wordsData;
    this.currentIndex = 0;
    this.modal = null;
    this.isOpen = false;
    this.createModal();
    this.attachEvents();
  }

  createModal() {
    // モーダルコンテナを作成
    this.modal = document.createElement('div');
    this.modal.className = 'word-modal';
    this.modal.style.cssText = `
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      z-index: 50000;
      justify-content: center;
      align-items: center;
    `;

    // モーダルコンテンツ
    const content = document.createElement('div');
    content.className = 'word-modal-content';
    content.style.cssText = `
      background: white;
      padding: 40px;
      max-width: 600px;
      width: 90%;
      max-height: 80vh;
      overflow-y: auto;
      position: relative;
      border-radius: 8px;
    `;

    // クローズボタン
    const closeBtn = document.createElement('button');
    closeBtn.className = 'word-modal-close';
    closeBtn.innerHTML = '×';
    closeBtn.style.cssText = `
      position: absolute;
      top: 15px;
      right: 20px;
      font-size: 32px;
      background: none;
      border: none;
      cursor: pointer;
      color: #666;
    `;

    // タイトル
    const title = document.createElement('h2');
    title.className = 'word-modal-title';
    title.style.cssText = `
      margin: 0 0 20px 0;
      font-size: 28px;
      color: #333;
    `;

    // 説明文
    const description = document.createElement('p');
    description.className = 'word-modal-description';
    description.style.cssText = `
      font-size: 16px;
      line-height: 1.6;
      color: #666;
      margin-bottom: 30px;
    `;

    // ナビゲーションコンテナ
    const nav = document.createElement('div');
    nav.className = 'word-modal-nav';
    nav.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 20px;
    `;

    // 前へボタン
    const prevBtn = document.createElement('button');
    prevBtn.className = 'word-modal-prev';
    prevBtn.innerHTML = '← 前へ';
    prevBtn.style.cssText = `
      padding: 10px 20px;
      background: #f0f0f0;
      border: none;
      cursor: pointer;
      border-radius: 4px;
      font-size: 14px;
    `;

    // カウンター
    const counter = document.createElement('span');
    counter.className = 'word-modal-counter';
    counter.style.cssText = `
      font-size: 14px;
      color: #999;
    `;

    // 次へボタン
    const nextBtn = document.createElement('button');
    nextBtn.className = 'word-modal-next';
    nextBtn.innerHTML = '次へ →';
    nextBtn.style.cssText = `
      padding: 10px 20px;
      background: #f0f0f0;
      border: none;
      cursor: pointer;
      border-radius: 4px;
      font-size: 14px;
    `;

    // 要素を組み立て
    nav.appendChild(prevBtn);
    nav.appendChild(counter);
    nav.appendChild(nextBtn);

    content.appendChild(closeBtn);
    content.appendChild(title);
    content.appendChild(description);
    content.appendChild(nav);

    this.modal.appendChild(content);
    document.body.appendChild(this.modal);

    // 要素の参照を保存
    this.elements = {
      title,
      description,
      counter,
      prevBtn,
      nextBtn,
      closeBtn
    };
  }

  attachEvents() {
    // クローズボタン
    this.elements.closeBtn.addEventListener('click', () => this.close());

    // 背景クリックで閉じる
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.close();
      }
    });

    // 前へボタン
    this.elements.prevBtn.addEventListener('click', () => this.navigate(-1));

    // 次へボタン
    this.elements.nextBtn.addEventListener('click', () => this.navigate(1));

    // ESCキーで閉じる
    document.addEventListener('keydown', (e) => {
      if (this.isOpen) {
        if (e.key === 'Escape') {
          this.close();
        } else if (e.key === 'ArrowLeft') {
          this.navigate(-1);
        } else if (e.key === 'ArrowRight') {
          this.navigate(1);
        }
      }
    });
  }

  open(index) {
    this.currentIndex = index;
    this.updateContent();
    this.modal.style.display = 'flex';
    this.isOpen = true;
  }

  close() {
    this.modal.style.display = 'none';
    this.isOpen = false;
  }

  navigate(direction) {
    this.currentIndex += direction;
    
    // ループ処理
    if (this.currentIndex < 0) {
      this.currentIndex = this.words.length - 1;
    } else if (this.currentIndex >= this.words.length) {
      this.currentIndex = 0;
    }

    this.updateContent();
  }

  updateContent() {
    const word = this.words[this.currentIndex];
    this.elements.title.textContent = word.title.replace(/\n/g, ' ');
    this.elements.description.innerHTML = (word.description || '説明はありません').replace(/<br>/gi, '<br>');
    this.elements.counter.textContent = `${this.currentIndex + 1} / ${this.words.length}`;
  }
}

// スタイル
const styles = [
  'style-pill-red',
  'style-pill-orange',
  'style-pill-teal',
  'style-pill-brown',
  'style-outline',
  'style-vertical'
];

// モーダルインスタンスを作成
const wordModal = new WordModal(words);
window.wordModal = wordModal; // グローバルに公開

// インスタンス作成
const wordPhysics = new WordPhysics({
  container: document.getElementById('wordsContainer'),
  words,
  styles
});

// イベントリスナー
window.addEventListener('load', () => {
  setTimeout(() => wordPhysics.init(), 100);
});

window.addEventListener('resize', () => {
  clearTimeout(window.resizeTimeout);
  window.resizeTimeout = setTimeout(() => wordPhysics.init(), 300);
});

const vertexShaderSource = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const fragmentShaderSource = `
precision highp float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_seed;

// Simplex noise
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                      -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m*m; m = m*m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);
  vec3 g;
  g.x  = a0.x  * x0.x  + h.x  * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  float time = u_time * 1.5; 
  
  // Use seed to offset noise sampling positions
  float seed = u_seed;
  
  // Multiple noise layers with randomized offsets
  float n1 = snoise(uv * 1.5 + time * 0.2 + seed) * 0.5 + 0.5;
  float n2 = snoise(uv * 0.7 - time * 0.15 + seed * 1.3 + 50.0) * 0.5 + 0.5;
  float n3 = snoise(uv * 2.0 + time * 0.18 + seed * 0.7 + 100.0) * 0.5 + 0.5;
  float n4 = snoise(uv * 0.5 + time * 0.08 + seed * 1.5 + 150.0) * 0.5 + 0.5;
  float n5 = snoise(uv * 1.0 + time * 0.12 + seed * 0.8 + 250.0) * 0.5 + 0.5;
  
  // Base colors
  vec3 hotPink = vec3(1.0, 0.4, 0.62);
  vec3 magenta = vec3(0.93, 0.35, 0.78);
  vec3 peach = vec3(1.0, 0.6, 0.45);
  vec3 warmYellow = vec3(1.0, 0.75, 0.4);  // New warm yellow
  vec3 softOrange = vec3(1.0, 0.65, 0.35); // New soft orange
  vec3 lavender = vec3(0.68, 0.5, 0.95);
  vec3 skyBlue = vec3(0.5, 0.68, 0.98);
  vec3 softPink = vec3(1.0, 0.7, 0.78);
  
  // Time-based morphing with random starting phase
  float phase = time * 0.2 + seed * 0.1;
  float scheme = mod(phase, 3.0);
  
  // Scheme 1: Pink to Lavender (Image 1)
  vec3 scheme1 = mix(hotPink, magenta, n1);
  scheme1 = mix(scheme1, lavender, n2 * (1.0 - uv.x * 0.6));
  scheme1 = mix(scheme1, softPink, n3 * 0.3);
  // Add yellow tint
  scheme1 = mix(scheme1, warmYellow, n5 * 0.25);
  
  // Scheme 2: Pink to Peach/Yellow (Image 2 - more yellow)
  vec3 scheme2 = mix(hotPink, magenta, n1 * 0.6);
  scheme2 = mix(scheme2, peach, (uv.x * 0.4 + uv.y * 0.3) * n2);
  scheme2 = mix(scheme2, warmYellow, n5 * 0.4);
  scheme2 = mix(scheme2, softOrange, n3 * 0.3);
  
  // Scheme 3: Blue to Pink to Peach (Image 3)
  vec3 scheme3 = mix(skyBlue, hotPink, n1 * 0.6 + uv.x * 0.4);
  scheme3 = mix(scheme3, peach, n2 * uv.x);
  scheme3 = mix(scheme3, softPink, (1.0 - uv.y) * 0.4);
  scheme3 = mix(scheme3, warmYellow, n5 * 0.3);
  
  // Smooth scheme transitions
  float t1 = smoothstep(0.0, 1.0, 1.0 - abs(scheme));
  float t2 = smoothstep(0.0, 1.0, 1.0 - abs(scheme - 1.0));
  float t3 = smoothstep(0.0, 1.0, 1.0 - abs(scheme - 2.0));
  
  if (scheme > 2.0) {
    t1 = smoothstep(0.0, 1.0, scheme - 2.0);
    t3 = smoothstep(0.0, 1.0, 3.0 - scheme);
  }
  
  float tTotal = t1 + t2 + t3 + 0.001;
  vec3 color = (scheme1 * t1 + scheme2 * t2 + scheme3 * t3) / tTotal;
  
  // Variable white veil with randomized noise
  float whiteNoise = snoise(uv * 1.2 - time * 0.12 + seed * 0.9 + 200.0) * 0.5 + 0.5;
  float whiteNoise2 = snoise(uv * 0.6 + time * 0.1 + seed * 1.1 + 300.0) * 0.5 + 0.5;
  
  float whiteAmount = 0.12 + whiteNoise * 0.32 + whiteNoise2 * 0.1;
  
  float saturationIsland = pow(n4, 0.7);
  whiteAmount *= (0.55 + saturationIsland * 0.75);
  whiteAmount = clamp(whiteAmount, 0.08, 0.52);
  
  color = mix(color, vec3(1.0), whiteAmount);
  
  // Moderate saturation boost
  vec3 gray = vec3(dot(color, vec3(0.299, 0.587, 0.114)));
  float satBoost = 1.15 + (1.0 - whiteAmount) * 0.45;
  color = mix(gray, color, satBoost);
  
  // Subtle bright glow spots
  float glow = pow(n3, 2.0) * 0.1;
  color = mix(color, vec3(1.0, 0.98, 0.99), glow);
  
  color = clamp(color, 0.0, 1.0);
  color = pow(color, vec3(0.96));
  
  gl_FragColor = vec4(color, 1.0);
}
`;

// Initialize
const canvas = document.getElementById('canvas');
const gl = canvas.getContext('webgl');

if (!gl) {
console.error('WebGL not supported');
}

// Create shaders
function createShader(gl, type, source) {
const shader = gl.createShader(type);
gl.shaderSource(shader, source);
gl.compileShader(shader);

if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
  console.error('Shader compile error:', gl.getShaderInfoLog(shader));
  gl.deleteShader(shader);
  return null;
}
return shader;
}

const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);

// Create program
const program = gl.createProgram();
gl.attachShader(program, vertexShader);
gl.attachShader(program, fragmentShader);
gl.linkProgram(program);

if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
console.error('Program link error:', gl.getProgramInfoLog(program));
}

gl.useProgram(program);

// Set up geometry
const positions = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
const positionBuffer = gl.createBuffer();
gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

const positionLocation = gl.getAttribLocation(program, 'a_position');
gl.enableVertexAttribArray(positionLocation);
gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

// Get uniform locations
const resolutionLocation = gl.getUniformLocation(program, 'u_resolution');
const timeLocation = gl.getUniformLocation(program, 'u_time');
const seedLocation = gl.getUniformLocation(program, 'u_seed');

// Random seed for this session
const seed = Math.random() * 1000;

// Resize handler
function resize() {
const dpr = window.devicePixelRatio || 1;
const width = window.innerWidth;
const height = window.innerHeight;
canvas.width = width * dpr;
canvas.height = height * dpr;
canvas.style.width = width + 'px';
canvas.style.height = height + 'px';
gl.viewport(0, 0, canvas.width, canvas.height);
}

resize();
window.addEventListener('resize', resize);

// Animation
const startTime = Date.now();

function render() {
const time = (Date.now() - startTime) / 1000;

gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
gl.uniform1f(timeLocation, time);
gl.uniform1f(seedLocation, seed);

gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

requestAnimationFrame(render);
}

render();